import React, { Component, ErrorInfo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCollectedLogs, getBrowserInfo } from "@/utils/logCollector";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  reportStatus: "idle" | "sending" | "sent" | "failed";
}

const CRASH_REPORT_COOLDOWN_KEY = "curio_last_crash_report";
const COOLDOWN_MS = 30_000;

function isDarkMode(): boolean {
  try {
    return localStorage.getItem("themeMode") === "dark";
  } catch {
    return false;
  }
}

/**
 * Root error boundary that prevents white screens by catching uncaught render
 * errors. Automatically inserts a crash report into the crash_reports table
 * with the error details, React component stack, and recent console logs.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    reportStatus: "idle",
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info.componentStack);
    this.submitCrashReport(error, info);
  }

  private async submitCrashReport(error: Error, info: ErrorInfo): Promise<void> {
    if (!this.shouldSendReport()) {
      this.setState({ reportStatus: "idle" });
      return;
    }

    this.setState({ reportStatus: "sending" });

    try {
      const consoleLogs = getCollectedLogs();
      const browserInfo = getBrowserInfo();

      let userId: string | null = null;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        userId = user?.id ?? null;
      } catch {
        // Auth unavailable (expired token, network issue) — continue without user_id
      }

      const row = {
        user_id: userId,
        error_name: error.name,
        error_message: error.message.slice(0, 2000),
        error_stack: error.stack?.slice(0, 8000) ?? null,
        component_stack: info.componentStack?.slice(0, 8000) ?? null,
        page_url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        console_logs: consoleLogs,
        browser_info: browserInfo,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: insertError } = await (supabase.from as any)("crash_reports").insert(row);

      if (insertError) {
        // If insert failed with a user_id (e.g. stale token), retry without it
        if (userId) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error: retryError } = await (supabase.from as any)("crash_reports").insert({
            ...row,
            user_id: null,
          });
          if (retryError) {
            console.error("[ErrorBoundary] Retry insert also failed:", retryError.message);
            this.setState({ reportStatus: "failed" });
            return;
          }
        } else {
          console.error("[ErrorBoundary] Failed to insert crash report:", insertError.message);
          this.setState({ reportStatus: "failed" });
          return;
        }
      }

      this.markReportSent();
      this.setState({ reportStatus: "sent" });
      console.info("[ErrorBoundary] Crash report saved to crash_reports table");
    } catch (submitError) {
      console.error("[ErrorBoundary] Error saving crash report:", submitError);
      this.setState({ reportStatus: "failed" });
    }
  }

  /**
   * Rate-limit automatic crash reports so a crash-loop doesn't flood the DB.
   */
  private shouldSendReport(): boolean {
    try {
      const last = localStorage.getItem(CRASH_REPORT_COOLDOWN_KEY);
      if (last && Date.now() - Number(last) < COOLDOWN_MS) {
        return false;
      }
    } catch {
      // localStorage unavailable — allow the report
    }
    return true;
  }

  private markReportSent(): void {
    try {
      localStorage.setItem(CRASH_REPORT_COOLDOWN_KEY, String(Date.now()));
    } catch {
      // Ignore localStorage errors
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { reportStatus, error } = this.state;
    const dark = isDarkMode();

    const bg = dark ? "#09090b" : "#fafafa";
    const fg = dark ? "#fafafa" : "#18181b";
    const muted = dark ? "#a1a1aa" : "#71717a";
    const codeBg = dark ? "#18181b" : "#f4f4f5";
    const codeBorder = dark ? "#27272a" : "#e4e4e7";

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily: "system-ui, -apple-system, sans-serif",
          backgroundColor: bg,
          color: fg,
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⚠️</div>

          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              marginBottom: "0.5rem",
            }}
          >
            Něco se pokazilo
          </h1>

          <p
            style={{
              color: muted,
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            Aplikace narazila na neočekávanou chybu.
            {reportStatus === "sent" && " Chyba byla automaticky nahlášena."}
            {reportStatus === "sending" && " Odesílání hlášení..."}
            {reportStatus === "failed" && " Automatické hlášení se nepodařilo odeslat."}
          </p>

          {error && (
            <pre
              style={{
                textAlign: "left",
                backgroundColor: codeBg,
                border: `1px solid ${codeBorder}`,
                borderRadius: "0.5rem",
                padding: "0.75rem 1rem",
                fontSize: "0.75rem",
                color: "#dc2626",
                overflow: "auto",
                maxHeight: 160,
                marginBottom: "1.5rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {error.name}: {error.message}
            </pre>
          )}

          <button
            onClick={this.handleReload}
            style={{
              backgroundColor: "#6366f1",
              color: "#fff",
              border: "none",
              borderRadius: "0.5rem",
              padding: "0.625rem 1.5rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "background-color 0.15s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#4f46e5")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#6366f1")}
          >
            Načíst znovu
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
